const region = import.meta.env.VITE_COGNITO_REGION as string | undefined;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;

type AuthenticationResult = {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
};

type AuthResponse = {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: AuthenticationResult;
  AvailableChallenges?: string[];
};

export type CognitoTokens = {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
};

export class CognitoError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "CognitoError";
  }
}

export const cognitoConfigured = Boolean(region && clientId);

async function call<T>(operation: string, input: Record<string, unknown>): Promise<T> {
  if (!region || !clientId)
    throw new CognitoError("NOT_CONFIGURED", "Email sign-in is not configured in this build.");
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(input),
  });
  const payload = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const type =
      String(payload.__type ?? "COGNITO_ERROR")
        .split("#")
        .at(-1) ?? "COGNITO_ERROR";
    throw new CognitoError(type, String(payload.message ?? "Email sign-in could not continue."));
  }
  return payload as T;
}

function tokens(
  result: AuthenticationResult | undefined,
  existingRefreshToken?: string,
): CognitoTokens | undefined {
  if (!result?.AccessToken || !result.IdToken) return undefined;
  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? existingRefreshToken,
    expiresAt: Date.now() + (result.ExpiresIn ?? 900) * 1_000,
  };
}

export async function refreshCognitoTokens(refreshToken: string) {
  const response = await call<AuthResponse>("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: clientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  const authenticated = tokens(response.AuthenticationResult, refreshToken);
  if (!authenticated)
    throw new CognitoError("AUTHENTICATION_INCOMPLETE", "Your session could not be refreshed.");
  return authenticated;
}

export async function initiateEmailOtp(email: string) {
  const response = await call<AuthResponse>("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: clientId,
    AuthParameters: {
      USERNAME: email,
      PREFERRED_CHALLENGE: "EMAIL_OTP",
    },
  });
  const authenticated = tokens(response.AuthenticationResult);
  if (authenticated) return { tokens: authenticated } as const;
  if (
    response.ChallengeName === "SELECT_CHALLENGE" &&
    !response.AvailableChallenges?.includes("EMAIL_OTP")
  )
    throw new CognitoError(
      "EMAIL_OTP_NOT_AVAILABLE",
      "No email-code account is available for this address.",
    );
  if (response.ChallengeName !== "EMAIL_OTP" || !response.Session)
    throw new CognitoError("UNEXPECTED_CHALLENGE", "Email verification could not begin.");
  return { session: response.Session } as const;
}

export async function respondToEmailOtp(email: string, session: string, code: string) {
  const response = await call<AuthResponse>("RespondToAuthChallenge", {
    ChallengeName: "EMAIL_OTP",
    ClientId: clientId,
    Session: session,
    ChallengeResponses: { USERNAME: email, EMAIL_OTP_CODE: code },
  });
  const authenticated = tokens(response.AuthenticationResult);
  if (!authenticated)
    throw new CognitoError("AUTHENTICATION_INCOMPLETE", "The email code was not accepted.");
  return authenticated;
}

export async function signUpWithEmail(email: string) {
  return call<{ UserConfirmed?: boolean; Session?: string }>("SignUp", {
    ClientId: clientId,
    Username: email,
    UserAttributes: [{ Name: "email", Value: email }],
  });
}

export async function resendEmailSignUp(email: string) {
  return call<{ CodeDeliveryDetails?: { Destination?: string } }>("ResendConfirmationCode", {
    ClientId: clientId,
    Username: email,
  });
}

export async function confirmEmailSignUp(email: string, code: string, signUpSession?: string) {
  const confirmed = await call<{ Session?: string }>("ConfirmSignUp", {
    ClientId: clientId,
    Username: email,
    ConfirmationCode: code,
    ...(signUpSession ? { Session: signUpSession } : {}),
  });
  const response = await call<AuthResponse>("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: clientId,
    ...(confirmed.Session ? { Session: confirmed.Session } : {}),
    AuthParameters: {
      USERNAME: email,
      PREFERRED_CHALLENGE: "EMAIL_OTP",
    },
  });
  const authenticated = tokens(response.AuthenticationResult);
  if (authenticated) return { tokens: authenticated } as const;
  if (response.ChallengeName !== "EMAIL_OTP" || !response.Session)
    throw new CognitoError(
      "AUTHENTICATION_INCOMPLETE",
      "Your account is confirmed. Sign in again.",
    );
  return { session: response.Session } as const;
}
